import { useState, useEffect, useRef } from 'react';

interface Position {
    x: number;
    y: number;
}

interface DragOptions {
    initialPosition?: Position;
    onDragEnd?: (position: Position) => void;
}

export const useDraggable = (options: DragOptions = {}) => {
    const [position, setPosition] = useState<Position>(options.initialPosition || { x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartPos = useRef<Position>({ x: 0, y: 0 });
    const elementStartPos = useRef<Position>({ x: 0, y: 0 });

    const handleMouseDown = (e: React.MouseEvent) => {
        // Prevent dragging if clicking on interactive elements like buttons
        if ((e.target as HTMLElement).closest('button, input, textarea, .no-drag')) {
            return;
        }

        setIsDragging(true);
        dragStartPos.current = { x: e.clientX, y: e.clientY };
        elementStartPos.current = { ...position };

        // Prevent text selection while dragging
        document.body.style.userSelect = 'none';
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;

            const dx = e.clientX - dragStartPos.current.x;
            const dy = e.clientY - dragStartPos.current.y;

            setPosition({
                x: elementStartPos.current.x + dx,
                y: elementStartPos.current.y + dy,
            });
        };

        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
                document.body.style.userSelect = '';
                options.onDragEnd?.(position);
            }
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, position, options]);

    return {
        position,
        isDragging,
        handleMouseDown,
        setPosition
    };
};
